'use client';

import { useState } from 'react';
import { liveUpdateAttrs } from 'libs/contentful/utils';
import { getColorwayName } from 'utils/formatters';
import { cn } from 'utils/styles';
import CardContent from '../CardContent';
import CardWrapper from '../CardWrapper';
import { CardStyle, VideoVariantClassNames, VideoVariantProps } from '../types';
import { VideoControls, VideoPlayer, VideoPlayerProvider, VideoPreview } from '@/components/VideoPlayer';
import { Sys } from '@/libs/contentful/types';
import { YouTubeEmbedProps } from '@/components/shared/YouTubeEmbed/types';
import { VideoItemReference } from '@/types/sharedComponents';

const getYouTubeThumbnailUrl = (youTubeVideoUrl: string): string => {
  const videoIdMatch = youTubeVideoUrl.match(/(?:v=|\/shorts\/|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  const videoId = videoIdMatch?.[1];
  if (videoId) {
    return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
  }
  return '';
};

const youTubeEmbedToVideoReference = (embed: YouTubeEmbedProps & { sys?: Sys }): VideoItemReference => {
  const thumbnailUrl = getYouTubeThumbnailUrl(embed.youTubeVideoUrl);
  const videoIdMatch = embed.youTubeVideoUrl.match(/(?:v=|\/shorts\/|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  const videoId = videoIdMatch?.[1] ?? 'youtube-video';
  const sys: Sys = embed.sys ?? { id: `youtube-${videoId}` };
  return {
    __typename: 'VideoItem',
    sys,
    videoUrl: embed.youTubeVideoUrl,
    previewImage: {
      sys,
      url: thumbnailUrl,
      title: 'YouTube Video',
      description: '',
      contentType: 'image/jpeg',
      width: 480,
      height: 360,
    },
    aspectRatio: { aspectRatio: 0.5625 },
  };
};

const getVideoReference = (video: VideoVariantProps['video']): VideoItemReference | null => {
  if (video == null) {
    return null;
  }

  if ('youTubeVideoUrl' in video) {
    return youTubeEmbedToVideoReference(video);
  }

  return video;
};

const VideoVariant = ({ sys, index, total, color, cardStyle, headline, video, className }: VideoVariantProps) => {
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  const handlePlay = () => setIsVideoPlaying(true);
  const handlePause = () => setIsVideoPlaying(false);
  const handleEnded = () => setIsVideoPlaying(false);

  const internalClassNames: VideoVariantClassNames = {
    cardWrapper: {
      wrapper: cn(
        { 'pb-6': !video },
        { 'flex flex-col gap-6': cardStyle !== CardStyle.None },
        className?.cardWrapper?.wrapper,
      ),
      header: cn(className?.cardWrapper?.header),
      content: cn('h-full grow-1 flex flex-col gap-6 justify-between', className?.cardWrapper?.content),
    },
    cardContent: {
      wrapper: cn('flex-0 grow-2 shrink-0', className?.cardContent?.wrapper),
      header: {
        wrapper: cn(className?.cardContent?.header?.wrapper),
        eyebrow: cn(className?.cardContent?.header?.eyebrow),
        headline: cn(className?.cardContent?.header?.headline),
      },
      copy: cn(className?.cardContent?.copy),
    },
    transitions: {
      playing: cn(className?.transitions?.playing),
      paused: cn(className?.transitions?.paused),
    },
    media: {
      wrapper: cn('overflow-hidden grow-0 px-6 pb-6 sm:px-12 sm:pb-12', className?.media?.wrapper),
      content: cn('w-full h-full overflow-hidden rounded-lg', className?.media?.content),
    },
    preview: {
      label: cn('text-xs font-medium', className?.preview?.label),
    },
  };

  const testFixture = 'card-video';
  const videoReference = getVideoReference(video);

  return (
    <CardWrapper
      cardStyle={cardStyle}
      index={index}
      total={total}
      color={getColorwayName(color)}
      className={internalClassNames.cardWrapper}
      testId={testFixture}>
      <CardContent
        sys={sys}
        headline={headline}
        testId={testFixture}
        className={{
          ...internalClassNames.cardContent,
          wrapper: cn(
            internalClassNames.cardContent.wrapper,
            isVideoPlaying ? internalClassNames.transitions.playing : internalClassNames.transitions.paused,
          ),
        }}
      />

      {videoReference ? (
        <div className={internalClassNames.media.wrapper}>
          <VideoPlayerProvider>
            <VideoPlayer
              video={videoReference}
              testId={`${testFixture}-player`}
              classNames={{
                wrapper: internalClassNames.media.content,
                player: 'object-cover',
              }}
              onStart={handlePlay}
              onPlay={handlePlay}
              onPause={handlePause}
              onEnded={handleEnded}
              {...liveUpdateAttrs(videoReference.sys?.id, 'video')}>
              <VideoPreview
                image={videoReference.previewImage}
                showPreviewOnEnd
                classNames={internalClassNames.preview}
              />
              <VideoControls hideOnVideoStarted showOnVideoEnd position="center" />
            </VideoPlayer>
          </VideoPlayerProvider>
        </div>
      ) : null}
    </CardWrapper>
  );
};

export default VideoVariant;
